import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, FileText, Percent, Globe } from 'lucide-react';
import SEO from '../../components/SEO';
import SeoNav from '../../components/SeoNav';

const schema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What should a Philippine invoice include?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'A Philippine official receipt or invoice should include: your business name, address, and TIN (Tax Identification Number); the BIR Permit to Use; the client name and TIN; a unique invoice number; the date; a description of goods or services; the VAT-exclusive amount, 12% VAT, and the VAT-inclusive total. For exports and services to non-residents, the invoice must be marked zero-rated.',
      },
    },
    {
      '@type': 'Question',
      name: 'What is the VAT rate in the Philippines?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'The standard VAT rate in the Philippines is 12%. VAT registration is required for businesses with annual gross sales or receipts exceeding PHP 3,000,000. Businesses below this threshold are subject to the 3% percentage tax (or 1% under the Bayanihan Act). Export of goods and services to non-residents is zero-rated at 0% VAT.',
      },
    },
    {
      '@type': 'Question',
      name: 'How do Filipino freelancers invoice foreign clients?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Filipino freelancers providing services to overseas clients typically invoice in USD. Export of services to non-residents is zero-rated for VAT — you do not add 12% VAT on such invoices. Use the international invoice type to bill in USD or any currency with your Philippine bank account or remittance details.',
      },
    },
    {
      '@type': 'Question',
      name: 'Do freelancers in the Philippines need to pay VAT?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'If your annual gross sales or receipts are below PHP 3,000,000, you are not required to register for VAT. Instead, you pay a 3% percentage tax on gross receipts. Above PHP 3,000,000, VAT registration and charging of 12% VAT on taxable sales is mandatory. Services rendered to foreign clients are generally zero-rated regardless of your registration status.',
      },
    },
    {
      '@type': 'Question',
      name: 'Is this invoice generator free for Filipino users?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. Invilic is completely free for Filipino freelancers and businesses. Create professional invoices in USD, PHP, or any currency. Download a clean PDF instantly — no sign-up required for up to 5 invoices per month.',
      },
    },
  ],
};

const invoiceTypes = [
  {
    icon: Globe,
    title: 'International Invoice',
    subtitle: 'For billing overseas clients in USD',
    features: ['USD, GBP, EUR billing', 'Zero-rated export format', 'SWIFT / bank details', 'Multi-currency support', 'Professional PDF'],
    to: '/create/international',
    color: 'blue',
    btnClass: 'bg-blue-600 hover:bg-blue-700',
  },
  {
    icon: FileText,
    title: 'Standard Invoice',
    subtitle: 'For local clients in PHP',
    features: ['PHP currency', 'TIN number field', 'Custom line items', 'Logo upload', 'Payment terms'],
    to: '/create/freelancer',
    color: 'slate',
    btnClass: 'bg-slate-700 hover:bg-slate-800',
  },
  {
    icon: Percent,
    title: 'VAT Invoice',
    subtitle: 'For VAT-registered businesses',
    features: ['12% VAT auto-calculated', 'TIN number field', 'Net + VAT + total', 'BIR-ready format', 'PHP currency'],
    to: '/create/vat',
    color: 'indigo',
    btnClass: 'bg-indigo-600 hover:bg-indigo-700',
  },
];

export default function InvoiceGeneratorPhilippinesPage() {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <SEO
        title="Invoice Generator Philippines: Free PHP Invoice — BIR TIN, 12% VAT & GCash | Invilic"
        description="Free invoice generator for Filipino freelancers. PHP and USD invoices, 12% VAT auto-calc, BIR TIN fields, GCash and Payoneer payment. PDF download free, no sign-up."
        canonical="https://www.invilic.com/invoice-generator-philippines"
        schema={schema}
      />

      <SeoNav />

      <main className="max-w-4xl mx-auto px-6 py-16">
        <div className="text-center mb-16">
          <span className="inline-block bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wider mb-4">
            🇵🇭 Philippines
          </span>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 dark:text-white leading-tight mb-5">
            Invoice Generator Philippines
          </h1>
          <p className="text-xl text-gray-500 dark:text-gray-400 max-w-2xl mx-auto leading-relaxed mb-8">
            Free invoice generator for Filipino freelancers and businesses. Create professional invoices in USD, PHP, or any currency. Bill international clients with zero-rated export invoices or local clients in PHP. Download a clean PDF in seconds.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/create/international" className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-4 rounded-xl shadow-lg shadow-blue-600/20 transition-all duration-200">
              Bill International Clients <ArrowRight className="w-5 h-5" />
            </Link>
            <Link to="/create/freelancer" className="inline-flex items-center justify-center gap-2 bg-slate-700 hover:bg-slate-800 text-white font-semibold px-6 py-4 rounded-xl transition-all duration-200">
              Create PHP Invoice <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-3">Free · No sign-up · PDF in seconds</p>
        </div>

        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">Which Invoice Do You Need?</h2>
          <div className="grid sm:grid-cols-3 gap-5">
            {invoiceTypes.map(({ icon: Icon, title, subtitle, features, to, color, btnClass }) => (
              <div key={title} className={`border border-${color}-100 dark:border-${color}-800/30 rounded-2xl p-6 bg-${color}-50 dark:bg-${color}-900/10`}>
                <div className={`w-10 h-10 rounded-xl bg-${color}-100 dark:bg-${color}-900/40 flex items-center justify-center mb-4`}>
                  <Icon className={`w-5 h-5 text-${color}-600 dark:text-${color}-400`} />
                </div>
                <h3 className="font-bold text-gray-900 dark:text-white mb-1">{title}</h3>
                <p className={`text-xs text-${color}-700 dark:text-${color}-300 mb-4`}>{subtitle}</p>
                <ul className="space-y-2 mb-5">
                  {features.map(f => (
                    <li key={f} className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                      <CheckCircle className={`w-3.5 h-3.5 text-${color}-500 flex-shrink-0`} /> {f}
                    </li>
                  ))}
                </ul>
                <Link to={to} className={`inline-flex items-center justify-center gap-1.5 w-full px-4 py-2.5 ${btnClass} text-white text-sm font-semibold rounded-xl transition-all`}>
                  Create Free <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-16 p-8 rounded-2xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-5">International vs Local Invoice: Which Do You Need?</h2>
          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <div className="font-semibold text-blue-700 dark:text-blue-400 mb-3">Use an International Invoice if:</div>
              {[
                'Your client is overseas (US, UK, EU, Australia, etc.)',
                'You bill in USD, GBP, EUR, or another foreign currency',
                'Your services are zero-rated for export — no 12% VAT applies',
              ].map(p => (
                <div key={p} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300 mb-2">
                  <CheckCircle className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" /> {p}
                </div>
              ))}
            </div>
            <div>
              <div className="font-semibold text-slate-700 dark:text-slate-400 mb-3">Use a PHP Invoice if:</div>
              {[
                'Your client is based in the Philippines and pays in PHP',
                'You provide services to local businesses or individuals',
                'You need to include TIN and local business details',
              ].map(p => (
                <div key={p} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300 mb-2">
                  <CheckCircle className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" /> {p}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* BIR VAT + payment methods */}
        <section className="mb-16 grid sm:grid-cols-2 gap-6">
          <div className="p-6 rounded-2xl border border-indigo-100 dark:border-indigo-800/40 bg-indigo-50 dark:bg-indigo-900/20">
            <h2 className="font-bold text-gray-900 dark:text-white mb-3">12% VAT & BIR Registration</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
              Philippines VAT is administered by the Bureau of Internal Revenue (BIR). Key rules:
            </p>
            <ul className="space-y-2">
              {[
                'Standard VAT rate: 12% on taxable sales and services',
                'VAT registration required above PHP 3 million annual gross receipts',
                'Include TIN (Tax Identification Number) on all invoices',
                'Services to foreign non-resident clients: zero-rated (0% VAT)',
              ].map(item => (
                <li key={item} className="flex items-start gap-2 text-xs text-gray-600 dark:text-gray-400">
                  <CheckCircle className="w-3.5 h-3.5 text-indigo-500 flex-shrink-0 mt-0.5" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="p-6 rounded-2xl border border-blue-100 dark:border-blue-800/40 bg-blue-50 dark:bg-blue-900/20">
            <h2 className="font-bold text-gray-900 dark:text-white mb-3">Receiving International Payments</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
              Popular options for Filipino freelancers to receive USD payments:
            </p>
            <ul className="space-y-2">
              {[
                'Payoneer — withdraw to Philippine bank or GCash',
                'Wise — fast transfers, transparent exchange rates',
                'Bank wire (SWIFT) — direct to BDO, BPI, or UnionBank',
                'GCash / Maya — for local PHP payments only',
              ].map(item => (
                <li key={item} className="flex items-start gap-2 text-xs text-gray-600 dark:text-gray-400">
                  <CheckCircle className="w-3.5 h-3.5 text-blue-500 flex-shrink-0 mt-0.5" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">Philippines Invoice Generator: Frequently Asked Questions</h2>
          <div className="space-y-4">
            {schema.mainEntity.map(({ name, acceptedAnswer }) => (
              <details key={name} className="group border border-gray-100 dark:border-gray-800 rounded-xl overflow-hidden">
                <summary className="flex items-center justify-between px-6 py-4 cursor-pointer font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-900/50 list-none">
                  {name}
                  <ArrowRight className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-90 flex-shrink-0 ml-4" />
                </summary>
                <p className="px-6 pb-5 text-gray-500 dark:text-gray-400 text-sm leading-relaxed">{acceptedAnswer.text}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="text-center py-12 border-t border-gray-100 dark:border-gray-800">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">Create Your Philippine Invoice for Free</h2>
          <p className="text-gray-500 dark:text-gray-400 mb-8">Professional PDF in USD, PHP, or any currency. No account required.</p>
          <Link to="/create/international" className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-8 py-4 rounded-xl shadow-lg shadow-blue-600/20 transition-all duration-200 text-lg">
            Create Invoice Free <ArrowRight className="w-5 h-5" />
          </Link>
        </section>
      </main>
    </div>
  );
}
